import { Star, Quote } from "lucide-react";
import testimonialsData from "@/data/testimonials.json";
import { useInView } from "@/hooks/useInView";

interface Testimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  rating: number;
  text: string;
  location: string;
}

const TestimonialsSection = () => {
  const testimonials: Testimonial[] = testimonialsData;
  const { ref: headerRef, isInView: headerInView } = useInView({ threshold: 0.2 });
  const { ref: cardsRef, isInView: cardsInView } = useInView({ threshold: 0.1 });
  const { ref: statsRef, isInView: statsInView } = useInView({ threshold: 0.2 });

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={16}
        className={`${
          index < rating
            ? "fill-yellow-400 text-yellow-400"
            : "fill-gray-200 text-gray-200"
        }`}
      />
    ));
  };

  return (
    <section
      id="testimonials"
      className="py-20 bg-gradient-to-b from-white to-primary-light/30"
    >
      <div className="container-wide px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ${
            headerInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground mb-4">
            What Our Customers Say
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join thousands of satisfied customers who have transformed their
            daily meals with Revive Meal
          </p>
        </div>

        {/* Testimonials Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => {
            // Alternate animation directions: left, bottom, right, left, bottom, right
            const getAnimationClass = () => {
              if (index % 3 === 0) {
                return cardsInView 
                  ? "opacity-100 translate-x-0 scale-100" 
                  : "opacity-0 -translate-x-20 scale-95";
              } else if (index % 3 === 1) {
                return cardsInView 
                  ? "opacity-100 translate-y-0 scale-100" 
                  : "opacity-0 translate-y-20 scale-90";
              } else {
                return cardsInView 
                  ? "opacity-100 translate-x-0 scale-100" 
                  : "opacity-0 translate-x-20 scale-95";
              }
            };
            
            return (
            <div
              key={testimonial.id}
              className={`bg-white rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-1000 transform hover:-translate-y-2 hover:scale-105 ${
                getAnimationClass()
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Quote Icon */}
              <div className="mb-6">
                <Quote className="text-primary/20 w-12 h-12" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {renderStars(testimonial.rating)}
              </div>

              {/* Testimonial Text */}
              <p className="text-foreground/80 font-body mb-6 leading-relaxed">
                "{testimonial.text}"
              </p>

              {/* Customer Info */}
              <div className="flex items-center gap-4 pt-6 border-t border-border">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-14 h-14 rounded-full object-cover ring-2 ring-primary/10"
                  loading="lazy"
                />
                <div>
                  <h4 className="font-semibold font-heading text-foreground">
                    {testimonial.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.role}
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    {testimonial.location}
                  </p>
                </div>
              </div>
            </div>
          );
          })}
        </div>

        {/* Stats Section */}
        <div
          ref={statsRef}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          <div
            className={`text-center transition-all duration-1000 delay-100 ${
              statsInView
                ? "opacity-100 scale-100"
                : "opacity-0 scale-50"
            }`}
          >
            <div className="text-4xl md:text-5xl font-bold font-heading text-primary mb-2">
              500+
            </div>
            <div className="text-muted-foreground font-body">
              Happy Customers
            </div>
          </div>
          <div
            className={`text-center transition-all duration-1000 delay-300 ${
              statsInView
                ? "opacity-100 scale-100"
                : "opacity-0 scale-50"
            }`}
          >
            <div className="text-4xl md:text-5xl font-bold font-heading text-primary mb-2">
              4.9
            </div>
            <div className="text-muted-foreground font-body">
              Average Rating
            </div>
          </div>
          <div
            className={`text-center transition-all duration-1000 delay-500 ${
              statsInView
                ? "opacity-100 scale-100"
                : "opacity-0 scale-50"
            }`}
          >
            <div className="text-4xl md:text-5xl font-bold font-heading text-primary mb-2">
              30K+
            </div>
            <div className="text-muted-foreground font-body">
              Meals Delivered
            </div>
          </div>
          <div
            className={`text-center transition-all duration-1000 delay-700 ${
              statsInView
                ? "opacity-100 scale-100"
                : "opacity-0 scale-50"
            }`}
          >
            <div className="text-4xl md:text-5xl font-bold font-heading text-primary mb-2">
              98%
            </div>
            <div className="text-muted-foreground font-body">
              Satisfaction Rate
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
